import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Drawer,
  AppBar,
  Toolbar,
  Badge,
  Tooltip,
  ThemeProvider,
  createTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  SmartToy as AgentIcon,
  Person as PersonIcon,
  Upload as UploadIcon,
  AttachFile as AttachFileIcon,
  Description as DescriptionIcon,
  Menu as MenuIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Folder as FolderIcon,
  Edit as EditIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Check as CheckIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import designStudioService from '../../services/designStudio.service';
import DocumentPreview from './DocumentPreview';
import ScrollToTop from '../common/ScrollToTop';
import { colors as dizzarooColors } from '../../theme/tokens';

// Dizzaroo brand theme – Primary: Deep Blue #1E73BE
const designStudioTheme = createTheme({
  typography: {
    fontFamily: '"Urbanist", "Inter", "SF Pro Display", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h4: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    h5: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    h6: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    subtitle1: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
    subtitle2: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
    body1: { fontFamily: '"Urbanist", sans-serif' },
    body2: { fontFamily: '"Urbanist", sans-serif' },
    button: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
  },
  palette: {
    primary: {
      main: dizzarooColors.primary[500],
      light: dizzarooColors.primary.light,
      dark: dizzarooColors.primary[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main: dizzarooColors.accent.teal,
      light: dizzarooColors.accent.tealLight,
      dark: dizzarooColors.accent.tealDark,
      contrastText: '#ffffff',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700;800&display=swap');
      `,
    },
    MuiButton: {
      defaultProps: { color: 'primary', disableElevation: true },
      styleOverrides: {
        contained: {
          '&.MuiButton-colorPrimary': {
            backgroundColor: dizzarooColors.primary[500],
            '&:hover': { backgroundColor: dizzarooColors.primary[600] },
          },
        },
      },
    },
  },
});

const DesignStudioPage = ({ projectId }) => {
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const [sessionsDrawerOpen, setSessionsDrawerOpen] = useState(false);
  const [documentsDrawerOpen, setDocumentsDrawerOpen] = useState(false);
  const [documentPreviewOpen, setDocumentPreviewOpen] = useState(true);
  const [chatAreaOpen, setChatAreaOpen] = useState(true);
  const [structure, setStructure] = useState(null);
  const [structureLoading, setStructureLoading] = useState(false);
  const [savingStructure, setSavingStructure] = useState(false);
  const [structureCollapsed, setStructureCollapsed] = useState(false);
  const [includeProtocolContext, setIncludeProtocolContext] = useState(false);
  const [suggestingsections, setSuggestingSections] = useState(false);
  const [editingSectionIdx, setEditingSectionIdx] = useState(null);
  const [editingSectionText, setEditingSectionText] = useState('');
  const [newSectionText, setNewSectionText] = useState('');

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [session?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load sessions on mount
  useEffect(() => {
    if (projectId) {
      loadSessions();
    }
  }, [projectId]);

  const loadSessions = async () => {
    try {
      const response = await designStudioService.getSessions(projectId);
      setSessions(response.sessions || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  const createNewSession = async () => {
    if (!projectId) {
      setError('Project ID is required');
      return;
    }

    setCreatingSession(true);
    setError(null);
    try {
      const response = await designStudioService.createSession(projectId);
      setSession(response.session);
      if (response.session?.structure) {
        setStructure(response.session.structure);
      } else {
        setStructure(null);
      }
      await loadSessions();
    } catch (err) {
      console.error('Error creating session:', err);
      setError(err.response?.data?.message || 'Failed to create session');
    } finally {
      setCreatingSession(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await designStudioService.getSession(sessionId);
      setSession(response.session);
      if (response.session?.structure) {
        setStructure(response.session.structure);
      }
      if (response.session?.currentState !== 'structuring' && response.session?.structure) {
        setStructureCollapsed(true);
      } else if (response.session?.currentState === 'structuring') {
        setStructureCollapsed(false);
      }
    } catch (err) {
      console.error('Error loading session:', err);
      setError(err.response?.data?.message || 'Failed to load session');
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !session || loading) return;

    const userMessage = message.trim();
    setMessage('');
    setLoading(true);
    setError(null);

    try {
      const response = await designStudioService.sendMessage(session._id, userMessage, { includeProtocolContext });
      setSession(response.session);
      if (response.session?.structure) {
        setStructure(response.session.structure);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await designStudioService.deleteSession(sessionId);
      if (session?._id === sessionId) {
        setSession(null);
      }
      await loadSessions();
    } catch (err) {
      console.error('Error deleting session:', err);
      setError(err.response?.data?.message || 'Failed to delete session');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !session) return;

    setUploadingFile(true);
    setError(null);

    try {
      const response = await designStudioService.uploadDocument(session._id, file);
      setSession(response.session);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteUploadedDocument = async (documentId) => {
    if (!session) return;

    try {
      const response = await designStudioService.deleteUploadedDocument(session._id, documentId);
      setSession(response.session);
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err.response?.data?.message || 'Failed to delete document');
    }
  };

  const getAgentLabel = (agentType) => {
    const labels = {
      structure: 'Structure',
      question: 'Question Agent',
      prompt_generation: 'Prompt Generation Agent',
      approval: 'Approval Agent',
      completion: 'Completion Agent',
    };
    return labels[agentType] || 'Assistant';
  };

  const getAgentColor = (agentType) => {
    const colors = {
      structure: 'secondary',
      question: 'primary',
      prompt_generation: 'secondary',
      approval: 'primary',
      completion: 'primary',
    };
    return colors[agentType] || 'default';
  };

  // Auto-open document preview when document is generated
  useEffect(() => {
    if (session?.generatedDocument) {
      setDocumentPreviewOpen(true);
    }
  }, [session?.generatedDocument]);

  const isStructuring = session?.currentState === 'structuring';
  // Structure is locked once generation has started or completed — no edits allowed
  const isStructureLocked = ['generating', 'completed'].includes(session?.currentState);
  // Show the structure panel when:
  // - session is still in structuring state, OR
  // - structure has been saved at some point (collapsed or expanded edit)
  const hasStructure = !!(structure || session?.structure);
  const showStructurePanel = isStructuring || hasStructure;

  const inputDisabled =
    (isStructuring && !structureCollapsed) || savingStructure;
  const showSaveFirstHint = isStructuring && !structureCollapsed;
  const showReadyHint =
    session?.currentState === 'questioning' && structureCollapsed && hasStructure;

  const handleLocalStructureChange = (partial) => {
    setStructure((prev) => ({
      ...(prev || {}),
      ...partial,
    }));
  };

  const handleSuggestSections = async () => {
    const docType = structure?.documentType || 'protocol';
    const customName = structure?.customDocumentName || '';
    setSuggestingSections(true);
    try {
      const result = await designStudioService.suggestSections(docType, customName);
      if (result.sections && result.sections.length > 0) {
        handleLocalStructureChange({ sections: result.sections });
      }
    } catch (err) {
      console.error('Suggest sections error:', err);
    } finally {
      setSuggestingSections(false);
    }
  };

  const handleDeleteSection = (idx) => {
    const updated = (structure?.sections || []).filter((_, i) => i !== idx);
    handleLocalStructureChange({ sections: updated });
  };

  const handleStartEditSection = (idx) => {
    setEditingSectionIdx(idx);
    setEditingSectionText(structure?.sections?.[idx]?.title || '');
  };

  const handleConfirmEditSection = (idx) => {
    const sections = [...(structure?.sections || [])];
    if (editingSectionText.trim()) {
      sections[idx] = { ...sections[idx], title: editingSectionText.trim() };
    }
    handleLocalStructureChange({ sections });
    setEditingSectionIdx(null);
    setEditingSectionText('');
  };

  const handleAddSection = () => {
    if (!newSectionText.trim()) return;
    const sections = [...(structure?.sections || [])];
    sections.push({
      id: `section-${Date.now()}`,
      title: newSectionText.trim(),
      enabled: true,
      order: sections.length + 1,
    });
    handleLocalStructureChange({ sections });
    setNewSectionText('');
  };

  const handleSaveStructure = async () => {
    if (!session) return;
    setSavingStructure(true);
    setError(null);
    try {
      const docType = structure?.documentType || 'protocol';
      const payload = {
        documentType: docType,
        customDocumentName: structure?.customDocumentName || '',
        includeToc: typeof structure?.includeToc === 'boolean' ? structure.includeToc : true,
        header: {
          enabled: structure?.header?.enabled ?? true,
          text: structure?.header?.text || '',
        },
        footer: {
          enabled: structure?.footer?.enabled ?? false,
          text: structure?.footer?.text || '',
        },
        fonts: {
          titleSize: Number(structure?.fonts?.titleSize || 16),
          headingSize: Number(structure?.fonts?.headingSize || 14),
          bodySize: Number(structure?.fonts?.bodySize || 12),
          fontFamily: structure?.fonts?.fontFamily || 'Arial',
        },
        sections: Array.isArray(structure?.sections) ? structure.sections : [],
      };

      const response = await designStudioService.updateStructure(session._id, payload);
      if (response.structure) {
        setStructure(response.structure);
      }
      // Refetch session so we have latest currentState ('questioning') from the server
      const updated = await designStudioService.getSession(session._id);
      const latestSession = updated?.session || response.session;
      if (latestSession) {
        // Inject a visible welcome message into the session messages so the user
        // sees confirmation in the chat without a system message
        const docLabel =
          payload.documentType === 'custom'
            ? payload.customDocumentName || 'Custom document'
            : payload.documentType.toUpperCase();
        const sectionCount = payload.sections.length;
        const welcomeMsg = {
          role: 'assistant',
          content:
            `✅ Structure saved for **${docLabel}**` +
            (sectionCount > 0 ? ` with ${sectionCount} section${sectionCount > 1 ? 's' : ''}` : '') +
            `. Let's start generating the document!\n\nDescribe what you want — for example: "Generate ${docLabel} based on the uploaded reference document."`,
          agentType: null,
          timestamp: new Date().toISOString(),
        };
        setSession({
          ...latestSession,
          messages: [...(latestSession.messages || []), welcomeMsg],
        });
      }
      // After a successful save, collapse the structure panel into a compact summary
      setStructureCollapsed(true);
    } catch (err) {
      console.error('Error saving structure:', err);
      setError(err.response?.data?.message || 'Failed to save structure');
    } finally {
      setSavingStructure(false);
    }
  };

  return (
    <ThemeProvider theme={designStudioTheme}>
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Top Header Bar */}
      <AppBar 
        position="static" 
        elevation={0} 
        sx={{ 
          bgcolor: 'background.paper', 
          color: 'text.primary',
          borderBottom: '2px solid',
          borderColor: '#e0e0e0',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          position: 'relative',
          zIndex: 1100,
        }}
      >
        <Toolbar 
          sx={{ 
            justifyContent: 'space-between', 
            px: 3, 
            py: 1.5, 
            minHeight: '64px !important',
            borderBottom: '1px solid',
            borderColor: '#f0f0f0',
            bgcolor: 'rgba(0, 0, 0, 0.01)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setSessionsDrawerOpen(true)}
              sx={{ 
                mr: 1,
                '&:hover': {
                  bgcolor: 'rgba(22, 138, 173, 0.08)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                letterSpacing: '-0.01em',
              }}
            >
              Design Studio
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Protocol Context Indicator */}
            {session && includeProtocolContext && (
              <Tooltip title="Protocol context is active — click Reference Documents to toggle">
                <Chip
                  icon={<ArticleIcon sx={{ fontSize: '14px !important' }} />}
                  label="Protocol context ON"
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={() => setDocumentsDrawerOpen(true)}
                  sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                />
              </Tooltip>
            )}

            {/* Reference Documents Button */}
            {session && (
              <Tooltip title="Reference Documents">
                <IconButton
                  color="inherit"
                  onClick={() => setDocumentsDrawerOpen(true)}
                >
                  <Badge badgeContent={session.uploadedDocuments?.length || 0} color="primary">
                    <FolderIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {/* Document Preview Toggle */}
            {session?.generatedDocument && (
              <Tooltip title={documentPreviewOpen ? "Hide Document Preview" : "Show Document Preview"}>
                <IconButton
                  color="inherit"
                  onClick={() => setDocumentPreviewOpen(!documentPreviewOpen)}
                >
                  {documentPreviewOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </Tooltip>
            )}

            {/* New Session Button */}
            <Button
              variant="contained"
              onClick={createNewSession}
              disabled={creatingSession}
              startIcon={creatingSession ? <CircularProgress size={20} /> : <AgentIcon />}
              sx={{ ml: 1 }}
            >
              {creatingSession ? 'Creating...' : 'New Session'}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Content Area */}
      <Box 
        sx={{ 
          display: 'flex', 
          flex: 1, 
          overflow: 'hidden', 
          flexDirection: { xs: 'column', md: 'row' },
          bgcolor: 'background.default',
        }}
      >
        {/* Document Preview Sidebar (Left) - Responsive Width */}
        {session?.generatedDocument && documentPreviewOpen ? (
          <Paper
            sx={{
              width: { xs: '100%', sm: '100%', md: chatAreaOpen ? 600 : '100%', lg: chatAreaOpen ? 750 : '100%', xl: chatAreaOpen ? 1000 : '100%' },
              maxWidth: { xs: '100%', md: chatAreaOpen ? '60%' : '100%' },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              borderRight: { xs: 'none', md: chatAreaOpen ? '1px solid' : 'none' },
              borderBottom: { xs: '1px solid', md: 'none' },
              borderColor: 'divider',
              overflow: 'hidden',
              height: { xs: '50vh', md: '100%' },
              position: 'relative',
            }}
          >
            <Box
              sx={{
                p: { xs: 1.5, md: 2 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                <DescriptionIcon color="primary" sx={{ fontSize: { xs: 18, md: 24 } }} />
                Generated Document
              </Typography>
              <Tooltip title={chatAreaOpen ? "Hide Chat Area" : "Show Chat Area"}>
                <IconButton 
                  size="small" 
                  onClick={() => setChatAreaOpen(!chatAreaOpen)}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {chatAreaOpen ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <DocumentPreview 
                document={session.generatedDocument} 
                sessionId={session._id}
                projectId={projectId}
                structure={structure || session?.structure}
                onModifyRequest={(message) => {
                  // Auto-fill chat input with modification request (fallback path)
                  setMessage(message);
                }}
                onRegenerateSection={async (sectionId, prompt) => {
                  try {
                    const result = await designStudioService.regenerateSection(session._id, sectionId, prompt);
                    if (result && result.success && result.session) {
                      setSession(result.session);
                    }
                  } catch (error) {
                    console.error('Error regenerating section via Design Studio service:', error);
                  }
                }}
              />
            </Box>
          </Paper>
        ) : null}

        {/* Chat / Structure Area - Main Content (Right/Flexible) */}
        {chatAreaOpen ? (
          <Paper
            sx={{
              flex: session?.generatedDocument && documentPreviewOpen ? { xs: '0 0 50vh', md: 1 } : 1,
              minWidth: { xs: '100%', md: 400 },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              borderRadius: 0,
              height: { xs: session?.generatedDocument && documentPreviewOpen ? '50vh' : '100%', md: '100%' },
              position: 'relative',
            }}
          >
            {/* Show Document Preview Button - When Hidden */}
            {session?.generatedDocument && !documentPreviewOpen && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  display: { xs: 'none', md: 'flex' },
                }}
              >
                <Tooltip title="Show Document Preview" placement="right">
                  <IconButton
                    onClick={() => setDocumentPreviewOpen(true)}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '0 8px 8px 0',
                      boxShadow: 2,
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {!session ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <AgentIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
              <Typography variant="h6" color="text.secondary">
                Start a new session to begin
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create a session and ask questions to generate documents
              </Typography>
            </Box>
          ) : (
            <>
              {/* Structure Configuration Panel */}
              {showStructurePanel && (
                <>
                  {structureCollapsed ? (
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                          Structure:{' '}
                          {structure?.documentType === 'custom'
                            ? structure?.customDocumentName || 'Custom'
                            : (structure?.documentType || 'protocol').toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {Array.isArray(structure?.sections) && structure.sections.length > 0
                            ? `${structure.sections.length} sections • TOC ${structure?.includeToc ? 'on' : 'off'}`
                            : `TOC ${structure?.includeToc ? 'on' : 'off'}`}
                        </Typography>
                      </Box>
                      {isStructureLocked ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label="Structure locked"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setStructureCollapsed(false)}
                          >
                            View structure
                          </Button>
                        </Stack>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setStructureCollapsed(false)}
                        >
                          Edit structure
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Document Structure
                          </Typography>
                          {isStructureLocked && (
                            <Chip label="Locked — document generated" size="small" color="success" />
                          )}
                        </Box>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setStructureCollapsed(true)}
                        >
                          Close
                        </Button>
                      </Box>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <FormControl sx={{ minWidth: 180 }} size="small">
                          <InputLabel id="ds-document-type-label">Document Type</InputLabel>
                          <Select
                            labelId="ds-document-type-label"
                            label="Document Type"
                            disabled={isStructureLocked}
                            value={
                              ['protocol','icf','sap','custom'].includes(structure?.documentType)
                                ? structure.documentType
                                : 'protocol'
                            }
                            onChange={(e) =>
                              handleLocalStructureChange({ documentType: e.target.value })
                            }
                          >
                            <MenuItem value="protocol">Protocol</MenuItem>
                            <MenuItem value="icf">ICF</MenuItem>
                            <MenuItem value="sap">SAP</MenuItem>
                            <MenuItem value="custom">Custom</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Custom document name field */}
                        {(structure?.documentType === 'custom' ||
                          !['protocol','icf','sap'].includes(structure?.documentType)) && (
                          <TextField
                            label="Custom document name"
                            size="small"
                            fullWidth
                            placeholder="e.g. DSMB Charter, CRF, IB..."
                            disabled={isStructureLocked}
                            value={structure?.customDocumentName || ''}
                            onChange={(e) =>
                              handleLocalStructureChange({ customDocumentName: e.target.value })
                            }
                          />
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Switch
                            checked={structure?.includeToc ?? true}
                            disabled={isStructureLocked}
                            onChange={(e) =>
                              handleLocalStructureChange({ includeToc: e.target.checked })
                            }
                          />
                          <Typography variant="body2">Include TOC</Typography>
                        </Box>
                      </Stack>

                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          label="Header text"
                          fullWidth
                          size="small"
                          disabled={isStructureLocked}
                          value={structure?.header?.text || ''}
                          onChange={(e) =>
                            handleLocalStructureChange({
                              header: {
                                ...(structure?.header || {}),
                                text: e.target.value,
                                enabled: structure?.header?.enabled ?? true,
                              },
                            })
                          }
                        />
                        <TextField
                          label="Footer text"
                          fullWidth
                          size="small"
                          disabled={isStructureLocked}
                          value={structure?.footer?.text || ''}
                          onChange={(e) =>
                            handleLocalStructureChange({
                              footer: {
                                ...(structure?.footer || {}),
                                text: e.target.value,
                                enabled: structure?.footer?.enabled ?? false,
                              },
                            })
                          }
                        />
                      </Stack>

                      {/* Sections — LLM suggestion + editable chips */}
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Sections
                            {Array.isArray(structure?.sections) && structure.sections.length > 0 &&
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({structure.sections.length})
                              </Typography>
                            }
                          </Typography>
                          {!isStructureLocked && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={suggestingsections ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
                              onClick={handleSuggestSections}
                              disabled={suggestingsections}
                            >
                              {suggestingsections ? 'Generating…' : 'Suggest sections'}
                            </Button>
                          )}
                        </Box>

                        {/* Section chips */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            p: Array.isArray(structure?.sections) && structure.sections.length > 0 ? 1.5 : 0,
                            border: Array.isArray(structure?.sections) && structure.sections.length > 0 ? 1 : 0,
                            borderColor: 'divider',
                            borderRadius: 1,
                            minHeight: Array.isArray(structure?.sections) && structure.sections.length > 0 ? 48 : 0,
                            maxHeight: 260,
                            overflowY: 'auto',
                          }}
                        >
                          {Array.isArray(structure?.sections) && structure.sections.length > 0 ? (
                            structure.sections.map((sec, idx) => (
                              editingSectionIdx === idx && !isStructureLocked ? (
                                <Box key={sec.id || idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TextField
                                    size="small"
                                    autoFocus
                                    value={editingSectionText}
                                    onChange={(e) => setEditingSectionText(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleConfirmEditSection(idx);
                                      if (e.key === 'Escape') { setEditingSectionIdx(null); setEditingSectionText(''); }
                                    }}
                                    sx={{ width: 220 }}
                                  />
                                  <IconButton size="small" color="primary" onClick={() => handleConfirmEditSection(idx)}>
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Chip
                                  key={sec.id || idx}
                                  label={`${idx + 1}. ${sec.title}`}
                                  size="small"
                                  variant="outlined"
                                  onDelete={isStructureLocked ? undefined : () => handleDeleteSection(idx)}
                                  onClick={isStructureLocked ? undefined : () => handleStartEditSection(idx)}
                                  icon={isStructureLocked ? undefined : <EditIcon fontSize="small" />}
                                  sx={{ cursor: isStructureLocked ? 'default' : 'pointer', maxWidth: 280 }}
                                />
                              )
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {isStructureLocked ? 'No sections defined.' : 'No sections yet. Click "Suggest sections" to auto-generate, or add manually below.'}
                            </Typography>
                          )}
                        </Box>

                        {/* Add section row */}
                        {!isStructureLocked && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <TextField
                              size="small"
                              placeholder="Add a section title…"
                              fullWidth
                              value={newSectionText}
                              onChange={(e) => setNewSectionText(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleAddSection(); }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={handleAddSection}
                              disabled={!newSectionText.trim()}
                              startIcon={<AddIcon />}
                            >
                              Add
                            </Button>
                          </Box>
                        )}
                      </Box>

                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          label="Title font size"
                          type="number"
                          size="small"
                          disabled={isStructureLocked}
                          sx={{ width: { xs: '100%', md: 160 } }}
                          value={structure?.fonts?.titleSize ?? 16}
                          onChange={(e) =>
                            handleLocalStructureChange({
                              fonts: {
                                ...(structure?.fonts || {}),
                                titleSize: Number(e.target.value || 16),
                              },
                            })
                          }
                        />
                        <TextField
                          label="Heading font size"
                          type="number"
                          size="small"
                          disabled={isStructureLocked}
                          sx={{ width: { xs: '100%', md: 160 } }}
                          value={structure?.fonts?.headingSize ?? 14}
                          onChange={(e) =>
                            handleLocalStructureChange({
                              fonts: {
                                ...(structure?.fonts || {}),
                                headingSize: Number(e.target.value || 14),
                              },
                            })
                          }
                        />
                        <TextField
                          label="Body font size"
                          type="number"
                          size="small"
                          disabled={isStructureLocked}
                          sx={{ width: { xs: '100%', md: 160 } }}
                          value={structure?.fonts?.bodySize ?? 12}
                          onChange={(e) =>
                            handleLocalStructureChange({
                              fonts: {
                                ...(structure?.fonts || {}),
                                bodySize: Number(e.target.value || 12),
                              },
                            })
                          }
                        />
                        <TextField
                          label="Font family"
                          size="small"
                          select
                          disabled={isStructureLocked}
                          sx={{ width: { xs: '100%', md: 200 } }}
                          value={structure?.fonts?.fontFamily || 'Times New Roman'}
                          onChange={(e) =>
                            handleLocalStructureChange({
                              fonts: {
                                ...(structure?.fonts || {}),
                                fontFamily: e.target.value || 'Times New Roman',
                              },
                            })
                          }
                          SelectProps={{
                            native: false,
                          }}
                        >
                          <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                          <MenuItem value="Arial">Arial</MenuItem>
                          <MenuItem value="Calibri">Calibri</MenuItem>
                          <MenuItem value="Helvetica">Helvetica</MenuItem>
                          <MenuItem value="Georgia">Georgia</MenuItem>
                          <MenuItem value="Garamond">Garamond</MenuItem>
                        </TextField>
                      </Stack>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 1 }}>
                        {isStructureLocked && (
                          <Typography variant="caption" color="success.main">
                            Structure is locked after document generation begins.
                          </Typography>
                        )}
                        <Button
                          variant="contained"
                          onClick={handleSaveStructure}
                          disabled={savingStructure || isStructureLocked}
                        >
                          {savingStructure ? <CircularProgress size={20} /> : 'Save structure'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {/* Messages Area */}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {session.messages.map((msg, index) => {
                  // System messages are backend context only – never show them in the UI
                  if (msg.role === 'system') return null;

                  const isUser = msg.role === 'user';
                  const isAgent = msg.agentType !== null && msg.agentType !== undefined;

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: isUser ? 'flex-end' : 'flex-start',
                        gap: 1,
                      }}
                    >
                      {!isUser && (
                        <Avatar
                          sx={{
                            bgcolor: isAgent
                              ? getAgentColor(msg.agentType) + '.main'
                              : 'primary.main',
                            width: 32,
                            height: 32,
                          }}
                        >
                          {isAgent ? <AgentIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                        </Avatar>
                      )}

                      <Box
                        sx={{
                          maxWidth: '70%',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        {isAgent && (
                          <Chip
                            label={getAgentLabel(msg.agentType)}
                            size="small"
                            color={getAgentColor(msg.agentType)}
                            sx={{ alignSelf: 'flex-start', mb: 0.5 }}
                          />
                        )}

                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: isUser ? 'primary.main' : 'background.paper',
                            color: isUser ? 'primary.contrastText' : 'text.primary',
                          }}
                        >
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {msg.content}
                          </Typography>
                        </Paper>

                        <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Box>

                      {isUser && (
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                      )}
                    </Box>
                  );
                })}

                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      <AgentIcon fontSize="small" />
                    </Avatar>
                    <Paper sx={{ p: 2 }}>
                      <CircularProgress size={20} />
                    </Paper>
                  </Box>
                )}

                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area */}
              <Box
                sx={{
                  p: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                {showSaveFirstHint && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Save structure above to start the conversation.
                  </Typography>
                )}
                {showReadyHint && !showSaveFirstHint && (
                  <Typography variant="body2" color="primary.main" sx={{ mb: 1 }}>
                    Structure saved. Describe what you want to generate (e.g. &quot;Generate DSMB charter from uploaded protocol&quot;).
                  </Typography>
                )}
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder={
                      inputDisabled
                        ? 'Save structure above first...'
                        : 'Type your message...'
                    }
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={loading || inputDisabled}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={!message.trim() || loading || inputDisabled}
                    sx={{ minWidth: 100 }}
                  >
                    {loading ? <CircularProgress size={24} /> : <SendIcon />}
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
        ) : null}
      </Box>

      {/* Sessions Drawer (Left Side) */}
      <Drawer
        anchor="left"
        open={sessionsDrawerOpen}
        onClose={() => setSessionsDrawerOpen(false)}
        sx={{
          width: 320,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 320,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon />
              Previous Sessions
            </Typography>
            <IconButton size="small" onClick={() => setSessionsDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              createNewSession();
              setSessionsDrawerOpen(false);
            }}
            disabled={creatingSession}
            sx={{ mb: 2 }}
          >
            {creatingSession ? <CircularProgress size={24} /> : 'New Session'}
          </Button>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {sessions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                No sessions yet
              </Typography>
            ) : (
              <Stack spacing={1}>
                {sessions.map((s) => (
                  <Paper
                    key={s._id}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      backgroundColor: session?._id === s._id ? 'action.selected' : 'background.paper',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    onClick={() => {
                      loadSession(s._id);
                      setSessionsDrawerOpen(false);
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {s.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(s.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSession(s._id, e);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Reference Documents Drawer (Right Side - Small) */}
      <Drawer
        anchor="right"
        open={documentsDrawerOpen}
        onClose={() => setDocumentsDrawerOpen(false)}
        sx={{
          width: 360,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 360,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2, pt: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon />
              Reference Documents
            </Typography>
            <IconButton size="small" onClick={() => setDocumentsDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {session ? (
            <>
              {/* Protocol as Context Toggle */}
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  borderColor: includeProtocolContext ? 'primary.main' : 'divider',
                  bgcolor: includeProtocolContext ? 'primary.50' : 'background.paper',
                }}
              >
                <ArticleIcon
                  sx={{
                    mt: 0.25,
                    fontSize: 20,
                    color: includeProtocolContext ? 'primary.main' : 'text.secondary',
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Protocol as context
                    </Typography>
                    <Switch
                      size="small"
                      checked={includeProtocolContext}
                      onChange={(e) => setIncludeProtocolContext(e.target.checked)}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {includeProtocolContext
                      ? 'Protocol sections will be injected into every generation request as background context.'
                      : 'Toggle on to use the latest protocol sections as context for document generation.'}
                  </Typography>
                </Box>
              </Paper>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,.json"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                fullWidth
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                sx={{ mb: 2 }}
              >
                {uploadingFile ? <CircularProgress size={20} /> : 'Upload Document'}
              </Button>

              {session.uploadedDocuments && session.uploadedDocuments.length > 0 ? (
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <List>
                    {session.uploadedDocuments.map((doc) => (
                      <ListItem
                        key={doc._id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          py: 1.5,
                        }}
                      >
                        <DescriptionIcon sx={{ mr: 1.5, fontSize: 20, color: 'primary.main' }} />
                        <ListItemText
                          primary={doc.originalName}
                          secondary={`${(doc.fileSize / 1024).toFixed(1)} KB`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleDeleteUploadedDocument(doc._id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    No documents uploaded yet
                    <br />
                    <Typography variant="caption" component="span">
                      Upload PDF, DOCX, TXT, or JSON files to use as reference
                    </Typography>
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Start a session to upload reference documents
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Scroll to Top Button */}
      <ScrollToTop />
      </Box>
    </ThemeProvider>
  );
};

export default DesignStudioPage;

